import { worldProjection, MAP_HEIGHT, MAP_WIDTH } from './mapProjection'

export type MapViewport = {
  left: number
  top: number
  right: number
  bottom: number
}

export const FULL_MAP_VIEWPORT: MapViewport = {
  left: 0,
  top: 0,
  right: MAP_WIDTH,
  bottom: MAP_HEIGHT,
}

export const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max)

export const getVisibleMapViewport = (
  frame: HTMLElement,
  canvas: SVGSVGElement
): MapViewport | null => {
  const matrix = canvas.getScreenCTM()
  if (!matrix) return null

  const inverse = matrix.inverse()
  const frameRect = frame.getBoundingClientRect()
  const topLeft = new DOMPoint(frameRect.left, frameRect.top).matrixTransform(inverse)
  const bottomRight = new DOMPoint(frameRect.right, frameRect.bottom).matrixTransform(inverse)

  return {
    left: clamp(topLeft.x, 0, MAP_WIDTH),
    top: clamp(topLeft.y, 0, MAP_HEIGHT),
    right: clamp(bottomRight.x, 0, MAP_WIDTH),
    bottom: clamp(bottomRight.y, 0, MAP_HEIGHT),
  }
}

export const getMapTranslateExtent = (
  viewport: MapViewport,
  zoom: number
): [[number, number], [number, number]] => [
  // D3 constrains the entire SVG viewBox, so account for the part cropped by CSS.
  [-viewport.left / zoom, -viewport.top / zoom],
  [
    MAP_WIDTH + (MAP_WIDTH - viewport.right) / zoom,
    MAP_HEIGHT + (MAP_HEIGHT - viewport.bottom) / zoom,
  ],
]

export const clampMapCenter = (
  center: [number, number],
  zoom: number,
  viewport: MapViewport
): [number, number] => {
  const projected = worldProjection(center)
  const invert = worldProjection.invert?.bind(worldProjection)
  if (!projected || !invert) return center

  const x = clamp(
    MAP_WIDTH / 2 - projected[0] * zoom,
    viewport.right - MAP_WIDTH * zoom,
    viewport.left
  )
  const y = clamp(
    MAP_HEIGHT / 2 - projected[1] * zoom,
    viewport.bottom - MAP_HEIGHT * zoom,
    viewport.top
  )

  return invert([(MAP_WIDTH / 2 - x) / zoom, (MAP_HEIGHT / 2 - y) / zoom]) || center
}
